import { Alert, Card, Modal, Space, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { AssignmentConflictResponseDto } from '@evimo/client/models';
import { Button } from '~/app/components/common/Button';
import type { WizardConflictDecision, WizardData } from '../../../types';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

const { Text } = Typography;

export interface StepAssetsConflictModalProps {
  open: boolean;
  data: WizardData;
  unresolvedCount: number;
  columns: ColumnsType<AssignmentConflictResponseDto>;
  onCancel: () => void;
  onApply: () => void;
  onDecision: (assetId: string, decision: WizardConflictDecision) => void;
}

export function StepAssetsConflictModal({
  open,
  data,
  unresolvedCount,
  columns,
  onCancel,
  onApply,
  onDecision,
}: StepAssetsConflictModalProps) {
  const { t } = useTranslation('maintenance');

  return (
    <Modal
      open={open}
      width={860}
      title={t('wizard.assets.conflicts.modalTitle', {
        count: data.checkedConflicts.length,
      })}
      onCancel={onCancel}
      onOk={onApply}
      okText={t('wizard.assets.conflicts.applyDecisions')}
      okButtonProps={{ disabled: unresolvedCount > 0 }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message={t('wizard.assets.conflicts.description', {
            count: data.checkedConflicts.length,
            total: data.assetIds.length,
          })}
        />
        <EvimoTable<AssignmentConflictResponseDto>
          rowKey="assetId"
          columns={columns}
          dataSource={data.checkedConflicts}
          size="small"
          pagination={false}
        />
        {data.checkedConflicts.map((conflict) => (
          <Card key={conflict.assetId} size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>{conflict.assetName}</Text>
              <Text type="secondary">
                {t('wizard.assets.conflicts.currentPlan')}:{' '}
                {conflict.conflictingPlanName}
              </Text>
              <Text type="secondary">
                {t('wizard.assets.conflicts.newPlan')}: {data.name || data.code}
              </Text>
              <Space>
                <Button
                  type={
                    data.conflictDecisions[conflict.assetId] === 'DESELECT'
                      ? 'primary'
                      : 'default'
                  }
                  onClick={() => onDecision(conflict.assetId, 'DESELECT')}
                >
                  {t('wizard.assets.conflicts.keepCurrent')}
                </Button>
                <Button
                  type={
                    data.conflictDecisions[conflict.assetId] === 'REPLACE'
                      ? 'primary'
                      : 'default'
                  }
                  onClick={() => onDecision(conflict.assetId, 'REPLACE')}
                >
                  {t('wizard.assets.conflicts.useNew')}
                </Button>
              </Space>
            </Space>
          </Card>
        ))}
      </Space>
    </Modal>
  );
}
